import {
  OptimizelyProvider,
  OptimizelyContext,
  createInstance,
} from '@optimizely/react-sdk';
import React, { useContext, useState, useEffect } from 'react';
import { toLocale } from '@peloton/internationalize/models/locale';
import { PageSpinner } from '@ecomm/spinner';
import { datafiles } from './datafiles';
import { SdkKeys } from './enums';
import { getCurrentEnvironment } from './environment';

const AsyncClient: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { optimizely } = useContext(OptimizelyContext);
  const [clientReady, setClientReady] = useState(false);
  const [, setError] = useState();

  useEffect(() => {
    (async () => {
      try {
        if (optimizely) {
          await optimizely.onReady();
          setClientReady(true);
        }
      } catch (e) {
        // This allows errors to be caught by ErrorBoundary
        setError(() => {
          throw e;
        });
      }
    })();
  }, [optimizely]);

  if (!clientReady) {
    return <PageSpinner />;
  }

  return <>{children}</>;
};

const optimizelyCookieName = 'optimizelyEndUserId';
const authAppCookieName = 'authAppOptimizelyEndUserId';

const getCookieValue = (cookieName: string): string | null => {
  const maybeCookieMatch = document.cookie.match(
    new RegExp(`(^| )${cookieName}=([^;]+)`),
  );
  return maybeCookieMatch ? maybeCookieMatch[2] : null;
};

const toUserId = (): string => {
  try {
    const oneWeek = 60 * 60 * 24 * 7;
    const value = getCookieValue(authAppCookieName);
    if (value) {
      return value;
    }
    const randomUUID = (window.crypto as Crypto).randomUUID();
    document.cookie = `${authAppCookieName}=${randomUUID};max-age=${oneWeek}`;
    return randomUUID;
  } catch (_) {
    return 'userId';
  }
};

export const getUserIdFromCookie = () =>
  getCookieValue(optimizelyCookieName) ?? toUserId();

const getOptimizelyClient = () => {
  const environment = getCurrentEnvironment();
  const sdkKey = SdkKeys[environment];
  const datafile = datafiles[environment];

  return createInstance({
    sdkKey,
    datafile,
    logLevel: 3,
  });
};

const client = getOptimizelyClient();

const locale = toLocale();

const Provider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const id = React.useMemo(() => getUserIdFromCookie(), []);
  return (
    <OptimizelyProvider
      optimizely={client}
      user={{
        id,
        attributes: {
          locale,
        },
      }}
    >
      <AsyncClient>{children}</AsyncClient>
    </OptimizelyProvider>
  );
};

export default Provider;
